import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Send, Star, ArrowLeft, ImageIcon } from 'lucide-react';
import { useAuth } from '../features/auth/AuthContext';
import API_BASE from '../services/apiBase';
import { resolvePreviewUrl } from '../utils/images';
import StarRatingInput from '../components/StarRating';

const ReviewSubmit = () => {
  const location = useLocation();
  const { user } = useAuth();
  const isThankYou = location.pathname.endsWith('/thank-you');

  const [form, setForm] = useState({
    name: '',
    email: '',
    hblNo: '',
    rating: 0,
    comment: '',
    photo: null,
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(isThankYou);

  const photoPreview = useMemo(
    () => (form.photo ? resolvePreviewUrl(form.photo, API_BASE) : ''),
    [form.photo],
  );

  useEffect(() => {
    if (user?.name || user?.fullName) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || user.fullName || '',
        email: f.email || user.email || '',
      }));
    }
  }, [user]);

  useEffect(() => {
    setSubmitted(isThankYou);
  }, [isThankYou]);

  useEffect(() => () => {
    if (photoPreview && photoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(photoPreview);
    }
  }, [photoPreview]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0] || null;
    setForm((prev) => ({ ...prev, photo: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.rating) {
      setError('Please select a star rating.');
      return;
    }
    if (form.comment.trim().length < 10) {
      setError('Please write at least 10 characters about your experience.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name.trim());
      fd.append('email', form.email.trim());
      fd.append('rating', String(form.rating));
      fd.append('comment', form.comment.trim());
      if (form.hblNo.trim()) fd.append('hblNo', form.hblNo.trim().toUpperCase());
      if (form.photo) fd.append('photo', form.photo);

      const headers = {};
      if (user?.token) headers.Authorization = `Bearer ${user.token}`;

      const res = await fetch(`${API_BASE}/api/reviews`, {
        method: 'POST',
        headers,
        body: fd,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Could not submit review');
      setSubmitted(true);
      window.history.replaceState(null, '', '/review/thank-you');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="jet-review-page">
        <div className="jet-container jet-review-page__inner">
          <motion.div
            className="jet-review-thanks"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45 }}
          >
            <div className="jet-review-thanks__icon">
              <CheckCircle className="w-12 h-12" />
            </div>
            <h1>Thank you!</h1>
            <p>Thank you for your valuable feedback. We appreciate you taking the time to share your experience with us.</p>
            <div className="jet-review-thanks__actions">
              <Link to="/" className="jet-btn">Back to Home</Link>
              <Link to="/tracking" className="jet-btn jet-btn-secondary">Track shipment</Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="jet-review-page">
      <div className="jet-container jet-review-page__inner">
        <Link to="/" className="jet-review-back">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>

        <motion.div
          className="jet-review-form-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <div className="jet-review-form-card__head">
            <Star className="w-8 h-8 jet-review-form-card__star" aria-hidden />
            <h1>Share your experience</h1>
            <p>
              Share your experience with Jet Shipping Cargo. You can optionally include your HBL number and a photo.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="jet-review-form">
            <div className="jet-review-form__rating">
              <label>Your rating *</label>
              <StarRatingInput
                value={form.rating}
                onChange={(n) => setForm((f) => ({ ...f, rating: n }))}
                disabled={submitting}
              />
            </div>

            <div className="jet-form-row">
              <label htmlFor="review-name">Your name *</label>
              <input
                id="review-name"
                name="name"
                type="text"
                className="form-input"
                required
                maxLength={120}
                value={form.name}
                onChange={handleChange}
                disabled={submitting}
                placeholder="e.g. Kamal Perera"
              />
            </div>

            <div className="jet-form-row">
              <label htmlFor="review-email">Email <span className="jet-form-opt">(optional)</span></label>
              <input
                id="review-email"
                name="email"
                type="email"
                className="form-input"
                value={form.email}
                onChange={handleChange}
                disabled={submitting}
                placeholder="you@email.com"
              />
            </div>

            <div className="jet-form-row">
              <label htmlFor="review-hbl">HBL number <span className="jet-form-opt">(optional)</span></label>
              <input
                id="review-hbl"
                name="hblNo"
                type="text"
                className="form-input"
                maxLength={40}
                value={form.hblNo}
                onChange={handleChange}
                disabled={submitting}
                placeholder="e.g. JET-2024-00123"
                style={{ textTransform: 'uppercase' }}
              />
              <span className="jet-form-hint">Enter the HBL or collection number from your shipment</span>
            </div>

            <div className="jet-form-row">
              <label htmlFor="review-comment">Your review *</label>
              <textarea
                id="review-comment"
                name="comment"
                className="form-textarea"
                required
                minLength={10}
                maxLength={2000}
                rows={5}
                value={form.comment}
                onChange={handleChange}
                disabled={submitting}
                placeholder="Tell us about your shipment experience — delivery time, packaging, staff support..."
              />
              <span className="jet-form-hint">{form.comment.length}/2000</span>
            </div>

            <div className="jet-form-row jet-review-photo-block">
              <label>Photo <span className="jet-form-opt">(optional)</span></label>
              <p className="jet-form-hint" style={{ textAlign: 'left', marginBottom: '0.5rem' }}>
                Upload a delivery photo, package photo, or any image related to your experience
              </p>
              <div className="jet-review-photo-row">
                <div className="jet-review-photo-preview">
                  {photoPreview ? (
                    <img src={photoPreview} alt="Review photo preview" />
                  ) : (
                    <ImageIcon className="w-8 h-8" style={{ opacity: 0.35 }} aria-hidden />
                  )}
                </div>
                <label className="jet-btn jet-btn-secondary jet-review-photo-btn">
                  {form.photo ? 'Change photo' : 'Choose photo'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="jet-crm-upload__input"
                    onChange={handlePhotoChange}
                    disabled={submitting}
                  />
                </label>
              </div>
              {form.photo ? <span className="jet-form-hint">{form.photo.name}</span> : null}
            </div>

            {error ? <p className="jet-form-error" role="alert">{error}</p> : null}

            <button type="submit" className="jet-btn jet-review-submit" disabled={submitting}>
              {submitting ? 'Sending…' : (
                <>
                  Submit review <Send className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default ReviewSubmit;
